import Link from 'next/link';
import styled from 'styled-components';

import { ConditionalWrapper } from 'shared/components/ConditionalWrapper';
import { Image } from 'shared/components/Image';
import { IRelatedProductData } from 'productTemplate/types/FactsWindowData';
import { normalizeValue } from 'shared/utils/normalizeValue';
import { sendTrackingEvent } from 'tracking/utils/sendTrackingEvent';
import { variableFontRegular } from 'styles/theme';

interface IRelatedProducts {
  productTemplateSlug: string;
  products: Array<IRelatedProductData>;
  section: string;
  title: string;
  titleLink?: string;
  titleTag?: 'h2' | 'h3';
}

export const RelatedProducts = ({
  productTemplateSlug,
  products,
  section,
  title,
  titleLink,
  titleTag: TitleTag = 'h2',
}: IRelatedProducts) => {
  const isAllSneakers = products.every((product) => product.category === 'shoes');

  const handleHeaderTap = () => {
    sendTrackingEvent('PDP_SECTION_HEADER_TAP', {
      product_template_slug: productTemplateSlug,
      section,
      header_copy: title,
    });
  };

  const handleGridTap = ({ index, slug }) => {
    sendTrackingEvent('PDP_RECOMMENDED_GRID_TAP', {
      cell_index: index,
      header_copy: title,
      product_template_slug: slug,
      target_product_template_slug: productTemplateSlug,
      section,
    });
  };

  return (
    <RelatedProducts.Wrapper data-qa="related_products" key={productTemplateSlug}>
      <TitleTag data-qa="related_products_title">
        <ConditionalWrapper
          if={!!titleLink}
          with={(children) => (
            <Link
              href={titleLink}
              prefetch={false}
              passHref
              onClick={handleHeaderTap}
              role="link"
              onKeyDown={handleHeaderTap}
              tabIndex={0}
            >
              {children}
            </Link>
          )}
        >
          {title}
        </ConditionalWrapper>
      </TitleTag>

      <div className={`productsGrid ${isAllSneakers ? 'allSneakers' : ''}`}>
        {products.map(({ slug, title: productName, pictureUrl, category }, index) => (
          <div key={slug} className="product" data-qa="related_product_image_item">
            <Link
              href={`/${category === 'shoes' ? 'sneakers' : 'apparel'}/${slug}`}
              passHref
              prefetch={false}
              onClick={() => handleGridTap({ index, slug })}
              role="link"
              onKeyDown={() => handleGridTap({ index, slug })}
              tabIndex={0}
            >
              {/* in order to show the placeholder image when there is no image url, we need to set it to undefined, it cannot be an empty string */}
              <Image
                alt={normalizeValue(productName)}
                crop={category !== 'apparel'}
                sizes={!pictureUrl || pictureUrl.includes('/missing.png') ? '48px' : '70px'}
                src={pictureUrl || undefined}
              />
            </Link>
          </div>
        ))}
      </div>
    </RelatedProducts.Wrapper>
  );
};

RelatedProducts.Wrapper = styled.div`
  a {
    text-decoration: underline;

    &:hover {
      text-decoration: underline;
    }
  }

  h2,
  h3 {
    ${variableFontRegular({ size: '11.5px' })};
    margin: 16px;
    text-transform: capitalize;
  }

  .productsGrid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    grid-column-gap: 24px;
    grid-row-gap: 32px;
    padding: 16px 16px 36px;
    align-items: center;
  }

  .productsGrid.allSneakers {
    align-items: flex-end;
  }
`;
